/**
 * useTechniqueSearch Hook
 *
 * Provides MITRE ATT&CK technique search functionality with optimized filtering.
 * Used by TechniqueSearchStep component.
 *
 * Features:
 * - Pre-computed lowercase keys for efficient searching
 * - Module-level memoization (computed once per app load)
 * - Dropdown-ready options format with technique ID as badge
 * - "Add custom" option when no results found
 */

import { useMemo } from 'react';

import mitreData from '@/sections/attacks/attackPaths/data.json';

/**
 * MITRE technique data structure from data.json
 */
interface MitreTechnique {
  id: string;
  name: string;
  description: string;
  tested?: boolean;
  outcome?: string;
  subTechniques?: Array<{
    techniqueId: string;
    id: string;
    name: string;
    description: string;
    tested?: boolean;
    outcome?: string;
  }>;
}

/**
 * Tactic (category) data structure from data.json
 */
interface MitreTactic {
  id: string;
  name: string;
  description: string;
  techniques: MitreTechnique[];
}

/**
 * Searchable technique with pre-computed lowercase key
 */
interface SearchableTechnique {
  id: string;
  name: string;
  description: string;
  /** MITRE ATT&CK tactic/category name (e.g., "Reconnaissance", "Execution") */
  tactic: string;
  /** Pre-computed lowercase search key for efficient filtering */
  searchKey: string;
  /** Unique key for React lists (combines tactic + technique ID) */
  uniqueKey: string;
}

/**
 * Dropdown option format for technique selection
 */
export interface TechniqueSearchOption {
  /** Display label (technique name) */
  label: string;
  /** Option value (technique ID) */
  value: string;
  /** Badge display (technique ID for reference) */
  count?: string;
}

/**
 * Return type for useTechniqueSearch hook
 */
export interface UseTechniqueSearchReturn {
  /** Filtered techniques matching search */
  filteredTechniques: SearchableTechnique[];
  /** Formatted options for Dropdown component */
  dropdownOptions: TechniqueSearchOption[];
  /** Whether to show "Add custom" option (no results for search) */
  showAddOption: boolean;
}

// ============================================================================
// Module-level computation (executed once per app load)
// Performance: Eliminates toLowerCase() calls per keystroke via pre-computation
// ============================================================================

/**
 * Flattens all techniques from all tactics into an array with tactic associations.
 * Includes both parent techniques and sub-techniques.
 *
 * IMPORTANT: MITRE ATT&CK intentionally maps techniques to multiple tactics.
 * For example, T1078 (Valid Accounts) appears in Initial Access, Persistence,
 * Privilege Escalation, and Defense Evasion. Each tactic-technique pair is
 * preserved as a separate entry for full MITRE accuracy.
 *
 * uniqueKey combines tactic ID + technique ID to ensure React key uniqueness.
 *
 * Exported as MITRE_TECHNIQUES for use by useSearchableList hook.
 */
export const MITRE_TECHNIQUES: SearchableTechnique[] = (() => {
  const techniques: SearchableTechnique[] = [];

  (mitreData as MitreTactic[]).forEach(tactic => {
    tactic.techniques.forEach(technique => {
      // Add parent technique with tactic association
      techniques.push({
        id: technique.id,
        name: technique.name,
        description: technique.description,
        tactic: tactic.name,
        searchKey:
          `${technique.id} ${technique.name} ${tactic.name}`.toLowerCase(),
        uniqueKey: `${tactic.id}-${technique.id}`,
      });

      // Add sub-techniques with tactic association
      (technique.subTechniques || []).forEach(sub => {
        const subId = `${sub.techniqueId}${sub.id}`;
        techniques.push({
          id: subId,
          name: sub.name,
          description: sub.description,
          tactic: tactic.name,
          searchKey: `${subId} ${sub.name} ${tactic.name}`.toLowerCase(),
          uniqueKey: `${tactic.id}-${subId}`,
        });
      });
    });
  });

  return techniques;
})();

/**
 * Maximum number of techniques to display in dropdown
 */
const MAX_DISPLAY_RESULTS = 100;

/**
 * Hook for searching MITRE ATT&CK techniques.
 *
 * Uses pre-computed lowercase keys for efficient filtering. Techniques
 * are filtered client-side since the data is static and loaded with the app.
 *
 * @param searchValue - Current search input value
 * @returns Filtered techniques and dropdown options
 *
 * @example
 * ```tsx
 * function TechniqueSearchStep({ formData, setFormData }) {
 *   const [searchValue, setSearchValue] = useState('');
 *   const { dropdownOptions, showAddOption } = useTechniqueSearch(searchValue);
 *
 *   return (
 *     <Dropdown
 *       options={showAddOption
 *         ? [{ label: `Add '${searchValue}'`, value: '__custom__' }]
 *         : dropdownOptions}
 *       // ...
 *     />
 *   );
 * }
 * ```
 */
export function useTechniqueSearch(
  searchValue: string
): UseTechniqueSearchReturn {
  // Filter techniques based on search value
  const filteredTechniques = useMemo(() => {
    if (!searchValue) {
      // Return first N techniques when no search
      return MITRE_TECHNIQUES.slice(0, MAX_DISPLAY_RESULTS);
    }

    const lowerSearch = searchValue.toLowerCase();
    return MITRE_TECHNIQUES.filter(technique =>
      technique.searchKey.includes(lowerSearch)
    ).slice(0, MAX_DISPLAY_RESULTS);
  }, [searchValue]);

  // Transform to dropdown options format
  const dropdownOptions = useMemo(
    () =>
      filteredTechniques.map(technique => ({
        label: technique.name,
        value: technique.id,
        count: technique.id, // Shows ID as badge
      })),
    [filteredTechniques]
  );

  // Show "Add custom" option when search has value but no results
  const showAddOption = searchValue.length > 0 && dropdownOptions.length === 0;

  return {
    filteredTechniques,
    dropdownOptions,
    showAddOption,
  };
}

/**
 * Get technique details by ID
 *
 * @param techniqueId - MITRE ATT&CK technique ID (e.g., T1059)
 * @returns Technique details or undefined if not found
 */
export function getTechniqueById(
  techniqueId: string
): SearchableTechnique | undefined {
  return MITRE_TECHNIQUES.find(t => t.id === techniqueId);
}
